import { PublicKey } from "@solana/web3.js";
import { TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { ReactNode, useEffect, useState, createContext, useContext, FC } from "react";
import { useConnection } from "@solana/wallet-adapter-react";
import { useAppSelector } from "@axvdex/state";
import { selectWalletInfo } from "@axvdex/state/wallet/walletSelectors";

export type Mint = string;
export type AssetBalances = { [key: Mint]: number };

interface UserBalanceContextState {
  assetBalances: AssetBalances;
  triggerFetchUserAssetBalances: () => void;
  isBalanceLoading: boolean;
}

export const UserBalanceContext = createContext<UserBalanceContextState>({} as UserBalanceContextState);
export const useUserBalanceContext = () => {
  return useContext(UserBalanceContext);
};
export interface UserBalanceProviderProps {
  children: ReactNode;
}

export const UserBalanceProvider: FC<UserBalanceProviderProps> = ({ children }) => {
  const userBalance = useUserBalance();
  return <UserBalanceContext.Provider value={userBalance}>{children}</UserBalanceContext.Provider>;
};

export function useUserBalance() {
  const [assetBalances, setAssetBalances] = useState<AssetBalances>({});
  const [isBalanceLoading, setIsBalanceLoading] = useState(false);
  const { connection } = useConnection();
  const walletInfo = useAppSelector(selectWalletInfo);

  async function triggerFetchUserAssetBalances() {
    if (!walletInfo || !walletInfo.walletAddress) {
      return;
    }
    setIsBalanceLoading(true);
    try {
      const publicKey = new PublicKey(walletInfo?.walletAddress);

      // Retreive the token addresses for the connected wallet
      const assetsBalance: AssetBalances = {};
      const tokenAccounts = await connection.getParsedTokenAccountsByOwner(publicKey, {
        programId: TOKEN_PROGRAM_ID,
      });
      for (const account of tokenAccounts.value) {
        assetsBalance[account.account.data.parsed.info.mint] = Number(
          account.account.data.parsed.info.tokenAmount.uiAmount
        );
      }
      setAssetBalances(assetsBalance);
    } catch (error) {
      console.log("Error fetching user asset balances", error);
    } finally {
      setIsBalanceLoading(false);
    }
  }

  // Subscribe to the sale account
  useEffect(() => {
    if (!walletInfo || !walletInfo.walletAddress) {
      return;
    }

    // Init the sale
    triggerFetchUserAssetBalances();

    const publicKey = new PublicKey(walletInfo?.walletAddress);
    const clientSubscriptionId = connection.onAccountChange(publicKey, async () => {
      triggerFetchUserAssetBalances();
    });

    // Unsubscribe from the sale when the component unmounts
    return () => {
      connection.removeAccountChangeListener(clientSubscriptionId);
    };
  }, [walletInfo]);

  return { assetBalances, triggerFetchUserAssetBalances, isBalanceLoading };
}
